import { supabase } from './supabaseClient';
import { montoParaBD } from '../utils/validacion';

import { getNegocioId } from './negocioActual';

// Cuentas de dinero del negocio (PRODUCT_VISION cap. 5.16).
// Tarjeta de crédito: saldo negativo = deuda.
export const TIPOS_CUENTA = [
  { valor: 'efectivo', etiqueta: 'Efectivo', icono: '💵' },
  { valor: 'banco', etiqueta: 'Cuenta de banco', icono: '🏦' },
  { valor: 'pos', etiqueta: 'POS / Datáfono', icono: '💳' },
  { valor: 'tarjeta_credito', etiqueta: 'Tarjeta de crédito', icono: '🪙' },
  { valor: 'otro', etiqueta: 'Otra', icono: '📁' },
];

export const iconoTipo = (tipo) =>
  TIPOS_CUENTA.find((t) => t.valor === tipo)?.icono || '📁';

export async function obtenerCuentas({ incluirInactivas = false } = {}) {
  let query = supabase
    .from('cuentas')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('creado_en', { ascending: true });
  if (!incluirInactivas) query = query.eq('activo', true);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function crearCuenta({ nombre, tipo, saldoInicial }) {
  const { data, error } = await supabase.rpc('crear_cuenta', {
    p_negocio_id: getNegocioId(),
    p_nombre: nombre.trim(),
    p_tipo: tipo,
    p_saldo_inicial: parseFloat(saldoInicial) || 0,
  });
  if (error) throw error;
  return data;
}

export async function renombrarCuenta(id, nombre) {
  const { error } = await supabase
    .from('cuentas')
    .update({ nombre: nombre.trim() })
    .eq('id', id);
  if (error) throw error;
}

// Actualiza el nombre y/o el tipo de la cuenta. El saldo NO se toca aquí
// (para corregir saldo se usa ajustar_saldo_cuenta, que deja registro).
export async function actualizarCuenta(id, { nombre, tipo }) {
  const cambios = {};
  if (nombre !== undefined) cambios.nombre = nombre.trim();
  if (tipo !== undefined) cambios.tipo = tipo;
  const { error } = await supabase
    .from('cuentas')
    .update(cambios)
    .eq('id', id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
}

// No se borra (rompería el historial): se desactiva.
export async function desactivarCuenta(id) {
  const { error } = await supabase.from('cuentas').update({ activo: false }).eq('id', id);
  if (error) throw error;
}

export async function ajustarSaldo(cuentaId, nuevoSaldo, motivo) {
  const { error } = await supabase.rpc('ajustar_saldo_cuenta', {
    p_negocio_id: getNegocioId(),
    p_cuenta_id: cuentaId,
    p_nuevo_saldo: parseFloat(nuevoSaldo) || 0,
    p_motivo: motivo || 'Ajuste manual',
  });
  if (error) throw error;
}

export async function transferir({ origen, destino, monto, descripcion }) {
  const { error } = await supabase.rpc('registrar_transferencia', {
    p_negocio_id: getNegocioId(),
    p_cuenta_origen: origen,
    p_cuenta_destino: destino,
    p_monto: montoParaBD(monto, { nombre: 'monto a transferir' }),
    p_descripcion: descripcion || null,
  });
  if (error) throw error;
}

// Datos de una transferencia (por su referencia) para precargar el editor.
export async function obtenerTransferencia(referencia) {
  const { data, error } = await supabase
    .from('movimientos_cuenta')
    .select('cuenta_id, tipo, monto, descripcion')
    .eq('negocio_id', getNegocioId()).eq('referencia', referencia);
  if (error) throw error;
  const salida = (data || []).find((m) => m.tipo === 'transferencia_salida');
  const entrada = (data || []).find((m) => m.tipo === 'transferencia_entrada');
  return {
    origen: salida?.cuenta_id || '',
    destino: entrada?.cuenta_id || '',
    monto: Math.abs(Number((entrada || salida)?.monto || 0)),
    descripcion: (entrada || salida)?.descripcion || '',
  };
}

// Edita una transferencia (cuentas/monto) con reverso atómico.
export async function editarTransferencia(referencia, { origen, destino, monto, descripcion }) {
  const { error } = await supabase.rpc('editar_transferencia', {
    p_negocio_id: getNegocioId(),
    p_referencia: String(referencia),
    p_cuenta_origen: origen,
    p_cuenta_destino: destino,
    p_monto: montoParaBD(monto, { nombre: 'monto a transferir' }),
    p_descripcion: descripcion || null,
  });
  if (error) throw error;
}

// Elimina una transferencia (revierte ambos saldos).
export async function eliminarTransferencia(referencia) {
  const { error } = await supabase.rpc('eliminar_transferencia', {
    p_negocio_id: getNegocioId(),
    p_referencia: String(referencia),
  });
  if (error) throw error;
}

// Corrige el saldo inicial (apertura) de una cuenta; ajusta el saldo actual.
export async function editarSaldoInicial(cuentaId, nuevoMonto) {
  const { error } = await supabase.rpc('editar_saldo_inicial', {
    p_negocio_id: getNegocioId(),
    p_cuenta_id: cuentaId,
    p_nuevo_monto: montoParaBD(nuevoMonto, { nombre: 'saldo', permitirCero: true, negativos: true }),
  });
  if (error) throw error;
}

export async function obtenerMovimientosCuenta(cuentaId, limite = 50) {
  const { data, error } = await supabase
    .from('movimientos_cuenta')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .eq('cuenta_id', cuentaId)
    .order('creado_en', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data || [];
}
